/**
 * File Processing Hook for Data Upload
 * Manages file processing state and column detection logic
 */

import { useState, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import {
  isDateFormat,
  isTimeFormat,
  isDateTimeFormat,
  hasDummyTime,
  detectDelimiter,
  readFileContent
} from '../utils/dataUpload.utils';
import { splitCSVLine } from '../utils/csvLineParser';
import { isSupportedDelimiter } from '../utils/delimiterChoices';
import { FileValidationData, CHUNK_SIZE } from '../types/dataUpload.types';
import { useFileSizeLimit } from '@/hooks/useFileSizeLimit';
import { checkFileSizes, FileSizeCheck } from '../utils/fileSizeGuard';

interface DateTimeFormat {
  index: number;
  isDateTime: boolean;
  isDate: boolean;
  isTime: boolean;
  hasDummyTime: boolean;
}

interface UseFileProcessingProps {
  radioHasHeader: string;
  delimiter: string;
  manualColumnSelection: boolean;
  setSelectedColumn1: (value: string) => void;
  setSelectedColumn1Index: (value: number) => void;
  setSelectedColumn2: (value: string) => void;
  setSelectedColumn2Index: (value: number) => void;
  setSelectedColumn3: (value: string) => void;
  setSelectedColumn3Index: (value: number) => void;
  setHasSeparateDateAndTime: (value: boolean) => void;
}

export const useFileProcessing = (props: UseFileProcessingProps) => {
  const { t } = useTranslation('dataUpload');
  const { maxFileSizeBytes, formattedMaxSize } = useFileSizeLimit();

  const guardResultToError = useCallback(
    (result: FileSizeCheck): string | null => {
      if (result.kind === 'ok') return null;
      const sizeMB = (result.sizeBytes / (1024 * 1024)).toFixed(2);
      if (result.kind === 'single') {
        return t('errorMessages.fileSizeExceeded', {
          name: result.file.name,
          size: sizeMB,
          maxSize: formattedMaxSize,
        });
      }
      return t('errorMessages.totalFileSizeExceeded', {
        size: sizeMB,
        maxSize: formattedMaxSize,
      });
    },
    [t, formattedMaxSize]
  );

  const {
    radioHasHeader,
    delimiter,
    manualColumnSelection,
    setSelectedColumn1,
    setSelectedColumn1Index,
    setSelectedColumn2,
    setSelectedColumn2Index,
    setSelectedColumn3,
    setSelectedColumn3Index,
    setHasSeparateDateAndTime
  } = props;

  const [headers, setHeaders] = useState<string[]>([]);
  const [tableData, setTableData] = useState<string[][]>([]);
  const [processedTableData, setProcessedTableData] = useState<string[][]>([]);

  /**
   * Detects and sets column mappings based on data analysis
   */
  const detectAndSetColumns = useCallback((headerRow: string[], dataRows: string[][]) => {
    // Guard: Need at least one data row
    if (!dataRows || dataRows.length === 0) return;

    const firstDataRow = dataRows[0];

    const dateTimeFormats: DateTimeFormat[] = firstDataRow.map((cell, index) => ({
      index,
      isDateTime: isDateTimeFormat(cell),
      isDate: isDateFormat(cell),
      isTime: isTimeFormat(cell),
      hasDummyTime: hasDummyTime(cell)
    }));

    const dateTimeColumn = dateTimeFormats.find(col => col.isDateTime);
    const dateColumn = dateTimeFormats.find(col => col.isDate);
    const timeColumn = dateTimeFormats.find(col => col.isTime);

    // SMART DETECTION: Check if datetime column truly has dummy time by comparing multiple rows
    let dateTimeHasDummyTime = false;

    if (dateTimeColumn && dateTimeColumn.hasDummyTime) {
      const dateTimeIndex = dateTimeColumn.index;
      const firstDateTime = firstDataRow[dateTimeIndex];

      // Check up to 10 rows (or all available rows if less)
      const rowsToCheck = Math.min(10, dataRows.length);
      let timeChanges = false;

      for (let i = 1; i < rowsToCheck; i++) {
        if (dataRows[i] && dataRows[i][dateTimeIndex]) {
          const currentDateTime = dataRows[i][dateTimeIndex];
          if (currentDateTime !== firstDateTime) {
            timeChanges = true;
            break;
          }
        }
      }

      // It's dummy time only if time DOESN'T change across rows
      dateTimeHasDummyTime = !timeChanges;
    }

    // SMART DETECTION: If datetime column has dummy time (00:00) AND there's a separate time column,
    // treat them as separate date+time columns, not as a single datetime column
    const shouldUseSeparateColumns = Boolean(
      dateTimeHasDummyTime && timeColumn ||
      (!dateTimeColumn && dateColumn && timeColumn)
    );

    setHasSeparateDateAndTime(shouldUseSeparateColumns);

    if (shouldUseSeparateColumns) {
      // Use separate date and time columns
      let actualDateColumn = dateColumn;
      if (dateTimeHasDummyTime && dateTimeColumn) {
        actualDateColumn = dateTimeColumn;
      }

      if (actualDateColumn && timeColumn) {
        setSelectedColumn1(headerRow[actualDateColumn.index]);
        setSelectedColumn1Index(actualDateColumn.index);
        setSelectedColumn2(headerRow[timeColumn.index]);
        setSelectedColumn2Index(timeColumn.index);

        const valueColumnIndex = headerRow.findIndex((_, index) =>
          index !== actualDateColumn!.index && index !== timeColumn.index
        );
        if (valueColumnIndex !== -1) {
          setSelectedColumn3(headerRow[valueColumnIndex]);
          setSelectedColumn3Index(valueColumnIndex);
        }
      }
    } else if (dateTimeColumn) {
      // Use single datetime column
      setSelectedColumn1(headerRow[dateTimeColumn.index]);
      setSelectedColumn1Index(dateTimeColumn.index);

      const valueColumnIndex = headerRow.findIndex((_, index) =>
        index !== dateTimeColumn.index
      );
      if (valueColumnIndex !== -1) {
        setSelectedColumn2(headerRow[valueColumnIndex]);
        setSelectedColumn2Index(valueColumnIndex);
      }
    } else if (dateColumn && !timeColumn) {
      // Date-only file (e.g., daily aggregates): treat the date column as the time column
      // with implicit midnight; backend pandas handles this via format inference.
      setSelectedColumn1(headerRow[dateColumn.index]);
      setSelectedColumn1Index(dateColumn.index);

      const valueColumnIndex = headerRow.findIndex((_, index) =>
        index !== dateColumn.index
      );
      if (valueColumnIndex !== -1) {
        setSelectedColumn2(headerRow[valueColumnIndex]);
        setSelectedColumn2Index(valueColumnIndex);
      }
    }
  }, [
    setSelectedColumn1, setSelectedColumn1Index,
    setSelectedColumn2, setSelectedColumn2Index,
    setSelectedColumn3, setSelectedColumn3Index,
    setHasSeparateDateAndTime
  ]);

  /**
   * Re-runs automatic column detection
   */
  const reDetectColumns = useCallback(() => {
    if (tableData.length > 0) {
      const headerRow = radioHasHeader === 'ja' ? tableData[0] : headers;
      const dataStartIndex = radioHasHeader === 'ja' ? 1 : 0;

      // Get up to 10 data rows for accurate detection
      const dataRows = tableData.slice(dataStartIndex, dataStartIndex + 10);

      if (dataRows.length > 0) {
        detectAndSetColumns(headerRow, dataRows);
        return true;
      }
    }
    return false;
  }, [tableData, headers, radioHasHeader, detectAndSetColumns]);

  /**
   * Processes a single file and extracts preview data
   */
  const processFile = useCallback((file: File, index: number): Promise<string[][]> => {
    return new Promise((resolve, reject) => {
      const guardError = guardResultToError(checkFileSizes([file], maxFileSizeBytes));
      if (guardError) {
        reject(new Error(guardError));
        return;
      }

      let offset = 0;
      let buffer = '';
      let fileHeaders: string[] = [];
      let previewData: string[][] = [];
      let isFirstChunk = true;

      const reader = new FileReader();
      
      const readNextChunk = () => {
        const slice = file.slice(offset, offset + CHUNK_SIZE);
        reader.readAsText(slice);
      };

      reader.onload = (e) => {
        try {
          const chunk = e.target?.result as string;
          buffer += chunk;

          const lines = buffer.split('\n');
          buffer = lines.pop() || '';

          if (index === 0 && isFirstChunk) {
            // Guard: reject files with no non-blank lines before attempting any parsing
            const nonBlankLines = lines.filter((l) => l.trim().length > 0);
            if (nonBlankLines.length === 0) {
              reject(new Error(t('errorMessages.fileNoData', { name: file.name })));
              return;
            }
            // Replace lines with filtered version so the parsing logic below operates on real content
            lines.splice(0, lines.length, ...nonBlankLines);

            if (radioHasHeader === 'ja') {
              // File has headers
              fileHeaders = splitCSVLine(lines[0], delimiter)
                .map((header) => header.trim())
                .filter((header) => header.length > 0);

              setHeaders(fileHeaders);

              // Auto-detect columns - get up to 10 data rows for accurate detection
              const dataRows = lines
                .slice(1, 11)
                .map((row) => splitCSVLine(row, delimiter).map(cell => cell.trim()))
                .filter((row) => row.some((cell) => cell.length > 0));

              // Auto-detect columns only if manual selection is not enabled
              if (!manualColumnSelection && dataRows.length > 0) {
                detectAndSetColumns(fileHeaders, dataRows);
              }

              previewData = lines
                .slice(1, 6)
                .map((row) => splitCSVLine(row, delimiter).map((cell) => cell.trim()))
                .filter((row) => row.some((cell) => cell.length > 0));
            } else {
              // File has no headers - generate Column1, Column2, etc.
              const firstRow = splitCSVLine(lines[0], delimiter).map(cell => cell.trim());
              let columnCount = firstRow.length;
              while (columnCount > 0 && firstRow[columnCount - 1] === '') {
                columnCount--;
              }

              fileHeaders = Array.from({ length: columnCount }, (_, idx) => `Column${idx + 1}`);
              setHeaders(fileHeaders);

              const dataRows = lines
                .slice(0, 10)
                .map((row) => {
                  const cells = splitCSVLine(row, delimiter).map((cell) => cell.trim());
                  return cells.slice(0, columnCount);
                })
                .filter((row) => row.some((cell) => cell.length > 0));

              if (!manualColumnSelection && dataRows.length > 0) {
                detectAndSetColumns(fileHeaders, dataRows);
              }

              previewData = lines
                .slice(0, 5)
                .map((row) => {
                  const cells = splitCSVLine(row, delimiter).map((cell) => cell.trim());
                  return cells.slice(0, columnCount);
                })
                .filter((row) => row.some((cell) => cell.length > 0));
            }

            isFirstChunk = false;
          }

          offset += CHUNK_SIZE;
          if (offset < file.size) {
            readNextChunk();
          } else {
            const result = fileHeaders.length > 0 
              ? [fileHeaders, ...previewData] 
              : previewData;
            buffer = '';
            reader.abort();
            resolve(result);
          }
        } catch (error) {
          if (error instanceof Error && error.message.includes('Cannot read properties of undefined')) {
            reject(new Error(t('errorMessages.fileNoData', { name: file.name })));
          } else {
            reject(error);
          }
        }
      };

      reader.onerror = () => {
        const errorMessage = reader.error?.message || t('errorMessages.unknownError');
        reject(new Error(t('errorMessages.fileReadError', {
          name: file.name,
          error: errorMessage
        })));
      };

      readNextChunk();
    });
  }, [radioHasHeader, delimiter, manualColumnSelection, detectAndSetColumns, t, guardResultToError, maxFileSizeBytes]);

  /**
   * Validates multiple files for consistency
   */
  const validateFiles = useCallback(async (
    selectedFiles: File[],
    selectedDelimiter: string
  ): Promise<{ valid: boolean; error?: string }> => {
    if (selectedFiles.length === 0) {
      return { valid: false, error: t('errorMessages.noFile') };
    }

    if (!selectedDelimiter) {
      return { valid: false, error: t('errorMessages.selectDelimiter') };
    }
    if (!isSupportedDelimiter(selectedDelimiter)) {
      return { valid: false, error: t('errorMessages.invalidDelimiter') };
    }

    const sizeGuard = guardResultToError(checkFileSizes(selectedFiles, maxFileSizeBytes));
    if (sizeGuard) {
      return { valid: false, error: sizeGuard };
    }

    try {
      if (selectedFiles.length > 1) {
        const fileValidationData: FileValidationData[] = [];

        for (const file of selectedFiles) {
          try {
            const fileContent = await readFileContent(file);
            const detectedDelimiter = detectDelimiter(fileContent);

            const lines = fileContent.split('\n').filter(line => line.trim().length > 0);
            let fileHeaders: string[] = [];
            let columnCount = 0;

            if (lines.length > 0) {
              const firstLine = lines[0];
              const columns = splitCSVLine(firstLine, detectedDelimiter).map(col => col.trim());
              columnCount = columns.length;

              if (radioHasHeader === 'ja') {
                fileHeaders = columns;
              }
            }

            fileValidationData.push({
              file,
              content: fileContent,
              detectedDelimiter,
              headers: fileHeaders,
              columnCount
            });
          } catch (fileError) {
            return {
              valid: false,
              error: t('errorMessages.fileReadError', {
                name: file.name,
                error: fileError instanceof Error ? fileError.message : t('errorMessages.unknownError')
              })
            };
          }
        }

        const firstFileData = fileValidationData[0];

        // Check delimiters match
        for (const fileData of fileValidationData) {
          if (fileData.detectedDelimiter !== selectedDelimiter) {
            return {
              valid: false,
              error: t('errorMessages.wrongDelimiter', {
                detected: t(`delimiterNames.${fileData.detectedDelimiter}`, { defaultValue: fileData.detectedDelimiter }),
                selected: t(`delimiterNames.${selectedDelimiter}`, { defaultValue: selectedDelimiter })
              }) + ' (' + fileData.file.name + ')'
            };
          }
        }

        // Reject single-column files (need at least time + value column)
        for (const fileData of fileValidationData) {
          if (fileData.columnCount < 2) {
            return {
              valid: false,
              error: t('errorMessages.singleColumn', { name: fileData.file.name })
            };
          }
        }

        // Check column counts match
        for (let i = 1; i < fileValidationData.length; i++) {
          const currentFileData = fileValidationData[i];
          if (currentFileData.columnCount !== firstFileData.columnCount) {
            return {
              valid: false,
              error: t('errorMessages.inconsistentColumnCount', {
                file1: firstFileData.file.name,
                count1: firstFileData.columnCount,
                file2: currentFileData.file.name,
                count2: currentFileData.columnCount
              })
            };
          }
        }

        // Check headers match
        if (radioHasHeader === 'ja' && firstFileData.headers.length > 0) {
          const firstHeadersStr = firstFileData.headers.join(',');
          for (let i = 1; i < fileValidationData.length; i++) {
            const currentFileData = fileValidationData[i];
            const currentHeadersStr = currentFileData.headers.join(',');
            if (firstHeadersStr !== currentHeadersStr) {
              return {
                valid: false,
                error: t('errorMessages.inconsistentHeaders', {
                  file1: firstFileData.file.name,
                  file2: currentFileData.file.name
                })
              };
            }
          }
        }
      } else {
        // Single file - validate delimiter
        const firstFile = selectedFiles[0];
        try {
          const fileContent = await readFileContent(firstFile);
          const detectedDelimiter = detectDelimiter(fileContent);

          if (detectedDelimiter !== selectedDelimiter) {
            return {
              valid: false,
              error: t('errorMessages.wrongDelimiter', {
                detected: t(`delimiterNames.${detectedDelimiter}`, { defaultValue: detectedDelimiter }),
                selected: t(`delimiterNames.${selectedDelimiter}`, { defaultValue: selectedDelimiter })
              }) + ' ' + t('errorMessages.selectCorrectDelimiter')
            };
          }

          // Reject single-column files (need at least time + value column)
          const firstNonBlankLine = fileContent
            .split(/\r?\n/)
            .find(line => line.trim().length > 0);
          if (firstNonBlankLine !== undefined) {
            const cols = firstNonBlankLine.split(selectedDelimiter).length;
            if (cols < 2) {
              return {
                valid: false,
                error: t('errorMessages.singleColumn', { name: firstFile.name })
              };
            }
          }
        } catch (fileError) {
          return {
            valid: false,
            error: t('errorMessages.fileReadError', {
              name: firstFile.name,
              error: fileError instanceof Error ? fileError.message : t('errorMessages.unknownError')
            })
          };
        }
      }

      return { valid: true };
    } catch (error) {
      return {
        valid: false,
        error: error instanceof Error ? error.message : t('errorMessages.unknownError')
      };
    }
  }, [radioHasHeader, t, guardResultToError, maxFileSizeBytes]);

  /**
   * Resets all file processing state
   */
  const resetProcessing = useCallback(() => {
    setHeaders([]);
    setTableData([]);
    setProcessedTableData([]);
  }, []);

  return {
    headers,
    setHeaders,
    tableData,
    setTableData,
    processedTableData,
    setProcessedTableData,
    detectAndSetColumns,
    reDetectColumns,
    processFile,
    validateFiles,
    resetProcessing
  };
};
